import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import PropTypes from 'prop-types'
import Modal from 'react-modal'
import './styles.css'
import axios from 'axios'

import { calculateTotalHSD } from './HSDCalculator'
import { getTypeColor, calculateDayIntensity, calculateTotalTime, getIntensityBarColor, getIntensityDisplay } from './utils'
import { HealthDomainChart } from './charts'
import { ActivityModal } from './ActivityModal'
import { RemoveButton, StyledCard, StyledContainer, StyledSection } from './StyledSections'
import { addAdaptiveRecommendation, deleteAdaptiveRecommendation, updateAdaptiveRecommendation } from '../../../../../api/adaptiveCoach'
import DataStyle from '../../../../program/style/programData.module.sass'
import Confirm from '../../../../UI/confirmModal'

// Initialize react-modal
Modal.setAppElement('#root')

const rpeMapping = {
    'Low': 2,
    'Moderate': 5,
    'High': 8,
    'Very High': 10
}

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

// Update the formatScheduleData function to assign HSD values
const formatScheduleData = (schedule, allActivities) => {
    if (!schedule) return null

    const formattedSchedule = {
        light: {
            monday: [], tuesday: [], wednesday: [],
            thursday: [], friday: [], saturday: [], sunday: []
        }
    }

    Object.entries(schedule).forEach(([intensity, days]) => {
        Object.entries(days).forEach(([day, activities]) => {
            if (!Array.isArray(activities)) return

            formattedSchedule[intensity][day] = activities.map(activity => {
                let sourceActivity = allActivities.find(a => activity.hsd_activity && activity.hsd_activity[0] === a.name)
                if(!sourceActivity){
                    sourceActivity = allActivities.find(a => activity.category && activity.category.toLowerCase() === a.type)
                }
                const rpe = rpeMapping[activity.intensity] || 5
                return {
                    id: activity.id,
                    name: activity.name,
                    time: activity.time || 30,
                    rpe: rpe,
                    type: activity.type.toLowerCase() || 'aerobic',
                    hsd: sourceActivity ? sourceActivity.hsd : activity.hsd,
                    instanceId: Date.now(),
                    workout_type: activity.workout_type,
                    intensity: activity.intensity,
                    description: activity.description,
                    isAiActivity: activity.isAiActivity || false
                }
            })
        })
    })

    return formattedSchedule
}

const ProgramManager = ({
    onCloseModal,
    onSubmit,
    initialWeeklySchedule,
    initialHsdIntensity,
    copilotDashboard,
    personId,
    aiWorkoutsDate,
    aiWorkouts,
    updateAiWorkouts,
    editItemCallback,
    trackActivity
}) => {
    // Add validation check for copilotDashboard and its properties
    const validCopilotDashboard = useMemo(() => {
        return copilotDashboard && {
            allActivities: copilotDashboard.allActivities || [],
            heartRateZones: copilotDashboard.heartRateZones || {},
            activities: {
                rationale: (copilotDashboard.activities && copilotDashboard.activities.rationale) || '',
                likes: (copilotDashboard.activities && copilotDashboard.activities.likes) || [],
                increase: (copilotDashboard.activities && copilotDashboard.activities.increase) || [],
                dislikes: (copilotDashboard.activities && copilotDashboard.activities.dislikes) || [],
                decrease: (copilotDashboard.activities && copilotDashboard.activities.decrease) || []
            }
        }
    }, [copilotDashboard])

    // Add useMemo for expensive computations
    const memoizedWeeklySchedule = useMemo(() => ({
        light: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
        }
    }), [])

    // Modify INITIAL_HSD to match the structure of your data
    const INITIAL_HSD = {
        light: {
            bone: 0,
            aerobic_fitness: 0,
            balance: 0,
            muscular_fitness: 0,
            cardiometabolic: 0,
            body_composition: 0,
            cognitive_health: 0,
            movement_quality: 0
        }
    }

    const [weeklySchedule, setWeeklySchedule] = useState(
        initialWeeklySchedule ? formatScheduleData(initialWeeklySchedule, (copilotDashboard.allActivities) || []) : memoizedWeeklySchedule
    )
    const [allActivities, setAllActivities] = useState(
        (validCopilotDashboard && validCopilotDashboard.allActivities) || []
    )
    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [currentActivity, setCurrentActivity] = useState(null)
    const [hsdByIntensity, setHsdByIntensity] = useState(initialHsdIntensity || INITIAL_HSD)

    // Add state for current intensity at the top level
    const [currentIntensity, setCurrentIntensity] = useState('light')

    // Add new state for saving status
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [itemToBeDeleted, setItemToBeDeleted] = useState(null)

    // Update useEffect for HSD calculation with safety checks
    useEffect(() => {
        const newHsdByIntensity = calculateTotalHSD(weeklySchedule, validCopilotDashboard)
        if (!weeklySchedule || !validCopilotDashboard) return
        try {
            if (newHsdByIntensity && typeof newHsdByIntensity === 'object') {
                const hasValidValues = Object.values(newHsdByIntensity).some(intensity =>
                    Object.values(intensity).some(value => value >= 0)
                )
                if (hasValidValues) {
                    setHsdByIntensity(prev => {
                        return newHsdByIntensity
                    })
                } else {
                    console.warn('Calculated HSD contains all zero values')
                }
            } else {
                console.warn('Invalid HSD calculation result:', newHsdByIntensity)
            }
        } catch (error) {
            console.error('Error calculating HSD:', error)
        }
    }, [weeklySchedule, validCopilotDashboard])

    // Modify handleClose to include the API call
    const handleClose = async () => {
        try {
            setIsSaving(true)

            const update_data = {
                weekly_schedule: weeklySchedule,
                hsd_intensity: hsdByIntensity
            }

            await onSubmit(update_data)

        } catch (error) {
            console.error('Error saving exercise recommendation:', error)
            alert('Error saving exercise recommendation. Please try again.')
            return
        } finally {
            setIsSaving(false)
            onCloseModal()
        }
    }

    const handleDragEnd = useCallback(async(result) => {
        if (!result.destination) return

        const { source, destination, draggableId } = result
        const sourceDroppableId = source.droppableId
        const destDroppableId = destination.droppableId

        const getIntensityAndDay = (droppableId) => {
            const parts = droppableId.replace('day-', '').split('-')
            return {
                intensity: parts[0],
                day: parts[1]
            }
        }

        if (sourceDroppableId === 'activities' && destDroppableId.startsWith('day-')) {
            trackActivity('exercise_scheduler_activity_add', 'Exercise Scheduler Update Rate')
            setIsSaving(true)
            const { intensity, day } = getIntensityAndDay(destDroppableId)

            const activityId = draggableId.replace('activity-', '')
            const activity = allActivities.find(a => a.id.toString() === activityId.toString())

            if (!activity) {
                console.info('No activity found with id:', activityId)
                return
            }

            const newActivity = {
                ...activity,
                id: `${Date.now()}`,
                name: activity.name || 'Unnamed Activity',
                type: activity.type || 'aerobic',
                time: activity.default_duration || 30,
                heartRateZone: null,
                difficultyLevel: null,
                rpe: 2,
                hsd: activity.hsd,
                instanceId: Date.now()
            }

            setWeeklySchedule(prev => ({
                ...prev,
                [intensity]: {
                    ...prev[intensity],
                    [day]: [...prev[intensity][day], newActivity]
                }
            }))

            const updatedAiRec = {
                id: newActivity.id,
                category: toTitleCase(activity.type),
                description: '',
                duration: (activity.default_duration || 30) * 60,
                intensity: toTitleCase(activity.intensity || 'low'),
                workout_type: activity.name,
                published: false,
                isProgramItem: false,
                isSleepActivity: false,
                hsd_activity: [activity.name],
                day_of_week: days.indexOf(day)
            }

            aiWorkouts = [...aiWorkouts, updatedAiRec]
            
            const result = await addAdaptiveRecommendation(
                personId,
                aiWorkoutsDate,
                updatedAiRec
            )

            await updateAiWorkouts()
            setIsSaving(false)
        }
        // If dragging within the same intensity calendar or between intensity calendars
        else if (destDroppableId.startsWith('day-')&& sourceDroppableId !== destDroppableId) {
            trackActivity('exercise_scheduler_activity_drag', 'Exercise Scheduler Update Rate')
            setIsSaving(true)
            const source = getIntensityAndDay(sourceDroppableId)
            const dest = getIntensityAndDay(destDroppableId)

            setWeeklySchedule(prev => {
                // Create deep copy of the source day's activities
                let sourceActivities = [...prev[source.intensity][source.day]]
                // Remove the activity from the source
                const [movedActivity] = sourceActivities.filter((activity) => activity.id == draggableId)

                // Create deep copy of the destination day's activities
                const destActivities = [...prev[dest.intensity][dest.day]]
                // Insert the activity at the new position
                destActivities.splice(destination.index, 0, movedActivity)

                return {
                    ...prev,
                    [source.intensity]: {
                        ...prev[source.intensity],
                        [source.day]: prev[source.intensity][source.day].filter(activity => activity.id != draggableId),
                        [dest.day]: destActivities
                    }
                }
            })
            const targetWorkout = aiWorkouts.filter((workout) => workout.id == draggableId)[0]
            const updatedWorkout = {
                ...targetWorkout,
                day_of_week: days.indexOf(dest.day)
            }

            const result = await updateAdaptiveRecommendation(
                personId,
                aiWorkoutsDate,
                targetWorkout.id,
                updatedWorkout
            )

            await editItemCallback(targetWorkout.id, updatedWorkout)
            setIsSaving(false)
        }
    }, [allActivities])

    const openModal = (activity, day, intensity) => {
        setCurrentActivity({ ...activity, day })
        setModalIsOpen(true)
    }

    const closeModal = () => {
        setModalIsOpen(false)
        setCurrentActivity(null)
    }

    const toTitleCase = (str) => {
        return str.replace(
          /\w\S*/g,
          text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        )
      }

    const saveActivityDetails = async (updatedActivity) => {
        const { day, intensity, id, type, time, workout_type, isAiActivity, description } = updatedActivity
        updatedActivity = {
            ...updatedActivity,
            rpe: rpeMapping[intensity] || 5
        }
        const category = 'light'
        setWeeklySchedule((prev) => ({
            ...prev,
            [category]: {
                ...prev[category],
                [day]: prev[category][day].map((activity) =>
                    activity.id === id ? updatedActivity : activity
                )
            }
        }))
        try {
            // For AI recommendations, map it back to the backend format
            if(!isAiActivity) return
            setIsSaving(true)
            const originalRec = aiWorkouts.filter((activity) => activity.id === id)[0]
            const updatedAiRec = {
                ...originalRec,
                category: toTitleCase(type),
                description : description || '',
                duration : time * 60,
                intensity : intensity,
                workout_type: workout_type,
                hsd_activity: [workout_type]
            }
            const result = await updateAdaptiveRecommendation(
                personId,
                aiWorkoutsDate,
                id,
                updatedAiRec
            )
            updateAiWorkouts()
            setIsSaving(false)
        } catch (err) {
            console.error(err)
            setIsSaving(false)
        }
    }

    const handleRemoveActivity = async (intensity, day, activityId) => {
        trackActivity('exercise_scheduler_activity_delete', 'Exercise Scheduler Update Rate')
        setItemToBeDeleted({
            day: day,
            id: activityId
        })
    }

    const convertData = (data) => {
        const result = {
            light: { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] }
        }

        const daysMap = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

        data.forEach(entry => {
            const day = daysMap[entry.day_of_week]
            const intensity = 'light'

            if (intensity && result[intensity][day] && entry.category !== 'Sleep') {
                result[intensity][day].push({
                    id: entry.id,
                    name: entry.workout_type,
                    time: entry.duration / 60,
                    workout_type: entry.workout_type || null,
                    difficultyLevel: entry.intensity || null,
                    heartRateZone: entry.heartRateZone || null,
                    rpe: entry.rpe || null,
                    hsd_activity: entry.hsd_activity,
                    intensity: entry.intensity || intensity,
                    type: entry.category || 'activity',
                    description: entry.description,
                    isAiActivity: true
                })
            }
        })
        return result
    }

    useEffect(() => {
        let workoutsToAdd = [...aiWorkouts], initialWeeklySchedule = {}
        if (aiWorkouts) {
            initialWeeklySchedule = convertData(workoutsToAdd)
        }
        setWeeklySchedule(formatScheduleData(initialWeeklySchedule, (copilotDashboard.allActivities) || []))
    }, [aiWorkouts])

    // Modify the renderCalendar function to include the total time display
    const renderCalendar = (intensityLevel) => {
        const totalTime = calculateTotalTime(weeklySchedule[intensityLevel.toLowerCase()])

        return (
            <div className="calendar-container">
                <div className="total-time-display">
                    Total Time: {totalTime} minutes
                </div>
                <div className="calendar-grid">
                    {Object.keys(weeklySchedule[intensityLevel.toLowerCase()]).map((day) => {
                        const dayActivities = weeklySchedule[intensityLevel.toLowerCase()][day]
                        const dayIntensity = calculateDayIntensity(dayActivities)

                        return (
                            <Droppable
                                key={`${intensityLevel}-${day}`}
                                droppableId={`day-${intensityLevel.toLowerCase()}-${day}`}
                                type="activity"
                            >
                                {(provided, snapshot) => (
                                    <div className="calendar-day-wrapper">
                                        <div
                                            className="intensity-bar"
                                            style={{
                                                width: '100%',
                                                height: `${Math.min(dayIntensity * 2, 80)}px`, // Height grows with dayIntensity
                                                maxHeight: '45px',
                                                backgroundColor: getIntensityBarColor(dayIntensity)
                                            }}
                                        />
                                        <div
                                            className="calendar-day"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            <h3 className="calendar-day-title">
                                                {day.charAt(0).toUpperCase() + day.slice(1)}
                                            </h3>
                                            {weeklySchedule[intensityLevel.toLowerCase()][day].map((activity, index) => (
                                                <Draggable
                                                    key={activity.id}
                                                    draggableId={`${String(activity.id).toString()}`}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            className="scheduled-activity"
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                backgroundColor: getTypeColor(activity.type),
                                                                minHeight: '60px'
                                                            }}
                                                        >
                                                            <RemoveButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleRemoveActivity(intensityLevel.toLowerCase(), day, activity.id)
                                                                }}
                                                            />
                                                            <div
                                                                onClick={() => openModal(activity, day, intensityLevel.toLowerCase())}
                                                                className="activity-content"
                                                            >
                                                                <span className="activity-name">{activity.name}</span>
                                                                <div className="activity-details">
                                                                    <span className="activity-duration">{activity.time}min</span>
                                                                    <span className="activity-intensity">{getIntensityDisplay(activity)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    </div>
                                )}
                            </Droppable>
                        )
                    })}
                </div>
            </div>
        )
    }

    // Update the HSD chart rendering with better validation
    const renderHsdChart = (intensity) => {
        if (!hsdByIntensity || !hsdByIntensity[intensity]) {
            return <p>No data available for {intensity} intensity activities</p>
        }

        const data = {
            labels: Object.keys(hsdByIntensity[intensity]).filter(key => key !== 'cardiometabolic'),
            values: Object.entries(hsdByIntensity[intensity])
                .filter(([key]) => key !== 'cardiometabolic')
                .map(([_, value]) => value === null ? 0 : Number(value) || 0)
        }

        return (
            <HealthDomainChart
                data={data}
                height={300}
                y_axis={400}
            />
        )
    }

    // Update the activities list section to include HSD plot
    const renderActivitiesList = (provided) => (
        <div className="activities-grid">
            <div className="activities-column">
                <div className="activities-group resistance">
                    <h4>Resistance</h4>
                    <div className="activities-row" ref={provided.innerRef} {...provided.droppableProps}>
                        {allActivities
                            .filter(activity => activity && activity.type === 'resistance')
                            .map((activity, index) => (
                                <Draggable
                                    key={`activity-${activity.id}`}
                                    draggableId={`activity-${activity.id}`}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            className={`activity-bubble draggable ${snapshot.isDragging ? 'dragging' : ''}`}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                backgroundColor: getTypeColor('resistance')
                                            }}
                                        >
                                            {activity.name || 'Unnamed Activity'}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                    </div>

                    <div className="activities-group aerobic">
                        <h4>Aerobic</h4>
                        <div className="activities-row">
                            {allActivities
                                .filter(activity => activity && activity.type === 'aerobic')
                                .map((activity, index) => (
                                    <Draggable
                                        key={`activity-${activity.id}`}
                                        draggableId={`activity-${activity.id}`}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                className={`activity-bubble draggable ${snapshot.isDragging ? 'dragging' : ''}`}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                    ...provided.draggableProps.style,
                                                    backgroundColor: getTypeColor('aerobic')
                                                }}
                                            >
                                                {activity.name || 'Unnamed Activity'}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                        </div>
                    </div>

                    <div className="activities-group activity">
                        <h4>Activity</h4>
                        <div className="activities-row">
                            {allActivities
                                .filter(activity => activity && activity.type === 'activity')
                                .map((activity, index) => (
                                    <Draggable
                                        key={`activity-${activity.id}`}
                                        draggableId={`activity-${activity.id}`}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <div
                                                className={`activity-bubble draggable ${snapshot.isDragging ? 'dragging' : ''}`}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={{
                                                    ...provided.draggableProps.style,
                                                    backgroundColor: getTypeColor('activity')
                                                }}
                                            >
                                                {activity.name || 'Unnamed Activity'}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="activities-chart-column">
                <div className="hsd-plot-container">
                    {renderHsdChart(currentIntensity)}
                </div>
            </div>
        </div>
    )

    const deleteItem = async () => {
        const intensity = 'light', day = itemToBeDeleted['day'], activityId = itemToBeDeleted['id']
        setWeeklySchedule(prev => ({
            ...prev,
            [intensity]: {
                ...prev[intensity],
                [day]: prev[intensity][day].filter(activity => activity.id !== activityId)
            }
        }))
        setIsDeleting(true)
        await deleteAdaptiveRecommendation(
            personId,
            aiWorkoutsDate,
            activityId
        )
        await updateAiWorkouts()
        setIsDeleting(false)
        setItemToBeDeleted(null)
    }

    if (!validCopilotDashboard) {
        return <div className="loading-state">Loading dashboard data...</div>
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            {isSaving ? (<div className={DataStyle.updatingOverlay}>
                <span>Updating..</span>
            </div>) : (<></>)}
            <StyledCard>
                <StyledContainer>
                    {/* Draggable Activities List and Weekly Schedule in the same frame */}
                    <StyledSection title="Activities and Schedule" style={{ display: 'flex', flexDirection: 'row', overflow: 'hidden', maxHeight: '100%' }}>
                        <Droppable droppableId="activities" direction="horizontal" type="activity">
                            {(provided) => renderActivitiesList(provided)}
                        </Droppable>

                        <div style={{ flex: 1, marginLeft: '20px' }}> {/* Adjust margin as needed */}
                            <StyledSection title="Weekly Schedule" style={{ marginTop: '0' }}> {/* Remove top margin */}
                                <div style={{
                                    textAlign: 'right',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '10px'
                                }}>
                                    {/* <button
                                        onClick={handleClose}
                                        className="save-button-ex"
                                        disabled={isSaving}
                                        style={{
                                            height: '30px',
                                            width: '100px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: 'none',
                                            opacity: isSaving ? 0.5 : 1,
                                            cursor: isSaving ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {isSaving ? (
                                            <span className="loading-spinner"></span>
                                        ) : 'Save'}
                                    </button> */}
                                </div>
                                {renderCalendar('light')}
                            </StyledSection>
                        </div>
                    </StyledSection>
                </StyledContainer>
            </StyledCard>
            {currentActivity && (
                <ActivityModal
                    isOpen={modalIsOpen}
                    onRequestClose={closeModal}
                    onSave={saveActivityDetails}
                    activity={currentActivity}
                    isAdaptiveCoach={true}
                    isSaving={isSaving}
                    allActivities={allActivities}
                />
            )}
            {itemToBeDeleted && (
              <Confirm
                {...{
                  modalWidth: 600,
                  title: 'Delete program item',
                  description: 'Do you want to delete this item?',
                  loading: isDeleting,
                  onConfirm: deleteItem,
                  onCancel: () => setItemToBeDeleted(null)
                }}
              />
            )}
        </DragDropContext>
    )
}

ProgramManager.propTypes = {
    onCloseModal: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    goals: PropTypes.object,
    lifestyleScore: PropTypes.object,
    hsd: PropTypes.object,
    initialWeeklySchedule: PropTypes.object,
    initialHsdIntensity: PropTypes.object,
    copilotDashboard: PropTypes.object.isRequired,
    person: PropTypes.object.isRequired,
    arDetail: PropTypes.object.isRequired
}

export default ProgramManager
